export type FilterIdentifier =
  | 'CHAINS_TABLE'
  | 'DOORS_TABLE'
  | 'CONTACTS_TABLE'
  | 'DEALS_TABLE'
  | 'DEALS_TABLE_V2'
  | 'ACTIVITY_TABLE_V2'
  | 'COMPANIES_TABLE'
  | 'UNMATCHED_DOORS_TABLE'
  | 'DEALS_PIPELINE_TABLE'
  | 'CNU_TABLE'
  | 'K12_TABLE'
  | 'HOSPITAL_TABLE'
  | 'NURSING_HOME_TABLE'
  | 'ALL_CAMPAIGNS_TABLE'
  | 'EMAILS_TABLE'
  | 'NURSING_HOME_TABLE'
  | 'REBATES_TABLE'
  | 'REBATE_CLAIMS_TABLE'
  | 'LODGING_TABLE'
  | 'LODGING_DOOR_TABLE'
  | 'CONTACT_REQUESTS_TABLE'
  | 'ATTRIBUTION_CODES_TABLE'
  | 'REBATE_ENROLLMENTS_TABLE'
  | 'CONVENIENCE_DOOR_TABLE'
  | 'CONVENIENCE_TABLE'
  | 'NOTES_TABLE'
  | 'SELECT_LOCATIONS'
  | 'DOOR_NOTES_TABLE'
  | 'REMINDERS_TABLE'
  | 'PRODUCTS_TABLE'
  | 'VARIANTS_TABLE'
  | 'AUTOMATIONS_TABLE'
  | 'DOCUMENTS_ALL_TABLE'
  | 'DOCUMENTS_PRODUCT_TABLE'
  | 'DOCUMENTS_COMPANY_TABLE'
  | 'DOCUMENTS_MODAL'
  | 'DOCUMENTS_IMAGE_PICKER'
  | 'MY_SAMPLES_TABLE'
  | 'SAMPLES_TABLE'
  | 'MOBILE_DOORS_LIST'
  | 'MOBILE_SAMPLES_TABLE'
  | 'MOBILE_REMINDERS_TABLE'
  | 'MOBILE_NOTES_TABLE'
  | 'SELECT_PRODUCT_VARIANT'
  | 'EMAIL_THREADS_TABLE'
  | 'DEAL_NOTES_TABLE'
  | 'DEAL_REMINDERS_TABLE'
  | 'DEAL_DOCUMENTS_TABLE'
  | 'USERS_TABLE'
  | 'MOBILE_COMPANY_SEARCH'

export const SavedViewTableLabels: Record<FilterIdentifier, string> = {
  CHAINS_TABLE: 'Restaurant Chains',
  DOORS_TABLE: 'Restaurant Doors',
  CONTACTS_TABLE: 'Contacts',
  DEALS_TABLE: 'Deals',
  DEALS_TABLE_V2: 'Deals',
  ACTIVITY_TABLE_V2: 'Activity',
  DEALS_PIPELINE_TABLE: 'Deals Pipeline',
  CNU_TABLE: 'College & Universities',
  K12_TABLE: 'K12 Districts',
  HOSPITAL_TABLE: 'Hospitals',
  COMPANIES_TABLE: 'Generic Company', // Wont be used
  UNMATCHED_DOORS_TABLE: 'Unmatched Doors', // Won't be used
  ALL_CAMPAIGNS_TABLE: 'Campaigns',
  NURSING_HOME_TABLE: 'Senior Living',
  EMAILS_TABLE: 'Emails',
  REBATES_TABLE: 'Rebates', // TODO rename to rebate_offers
  REBATE_CLAIMS_TABLE: 'Rebate Claims',
  LODGING_TABLE: 'Lodging Chains',
  LODGING_DOOR_TABLE: 'Lodging Doors',
  CONVENIENCE_DOOR_TABLE: 'Convenience Doors',
  CONVENIENCE_TABLE: 'Convenience Chains',
  CONTACT_REQUESTS_TABLE: 'Contact Requests',
  ATTRIBUTION_CODES_TABLE: 'Attribution Codes',
  REBATE_ENROLLMENTS_TABLE: 'Rebate Enrollments',
  NOTES_TABLE: 'Notes',
  SELECT_LOCATIONS: 'Select Locations for Deal',
  DOOR_NOTES_TABLE: 'Door Notes',
  REMINDERS_TABLE: 'Reminders',
  PRODUCTS_TABLE: 'Products',
  VARIANTS_TABLE: 'Variants',
  AUTOMATIONS_TABLE: 'Automations',
  DOCUMENTS_ALL_TABLE: 'Documents - All',
  DOCUMENTS_PRODUCT_TABLE: 'Documents - Product',
  DOCUMENTS_COMPANY_TABLE: 'Documents - Company',
  DOCUMENTS_MODAL: 'Documents - Modal',
  DOCUMENTS_IMAGE_PICKER: 'Documents - Image Picker',
  MY_SAMPLES_TABLE: 'My Samples',
  SAMPLES_TABLE: 'Samples',
  MOBILE_DOORS_LIST: 'Mobile Doors List',
  MOBILE_SAMPLES_TABLE: 'Mobile Samples',
  MOBILE_REMINDERS_TABLE: 'Mobile Reminders',
  MOBILE_NOTES_TABLE: 'Mobile Notes',
  SELECT_PRODUCT_VARIANT: 'Select Product Variant',
  EMAIL_THREADS_TABLE: 'Email Threads',
  DEAL_NOTES_TABLE: 'Deal Notes',
  DEAL_REMINDERS_TABLE: 'Deal Reminders',
  DEAL_DOCUMENTS_TABLE: 'Deal Documents',
  USERS_TABLE: 'Users',
  MOBILE_COMPANY_SEARCH: 'Mobile Company Search',
}

/**
 * Campaign target/contact tables key their FilterStore (active in-memory
 * filter state) per campaign by appending a `_CAMPAIGN_<id>` /
 * `_CAMPAIGN_V2_<id>` suffix to the base table identifier (see
 * `filterIdentifierModifier` in the campaign Targets/Contacts tabs). That
 * suffix is correct for isolating each campaign's *active* filters, but it
 * must NOT leak into the SavedView record: saved filters are meant to be
 * reusable across every campaign OF THE SAME TYPE (FIR-7494), which maps to
 * the base table identifier (e.g. `CHAINS_TABLE` for restaurant campaigns).
 *
 * This strips the per-campaign suffix so the saved-filter list query and the
 * save mutation both scope to the base table type. The backend already
 * constrains by company + (user | shared), so this never crosses tenants or
 * users: it only broadens across campaigns of the same type. Non-campaign
 * modifiers (tab/selection suffixes like `-selected`, `_<tab>`) are
 * intentionally left untouched.
 */
export function getSavedViewIdentifier(
  identifier: FilterIdentifier
): FilterIdentifier {
  return identifier.replace(/_CAMPAIGN(?:_V2)?_\d+$/, '') as FilterIdentifier
}

export interface SavedView {
  id: number
  name: string
  identifier: FilterIdentifier
  payload: Record<any, any>
  shared: boolean
  user: {
    first_name: string
    last_name: string
  }
  // The campaign this filter was auto-saved from on push-to-campaign
  // (FIR-7843); null for manually created filters. Campaign-linked filters
  // are renamed with their campaign and overwritten by repeat pushes, so the
  // UI badges them and warns before deletion.
  campaign: {
    id: number
    name: string
  } | null
  // Returned by SavedViewSerializer (ISO timestamps).
  created: string
  modified: string
}

export interface SavedViewUpdate {
  name: string
  shared: boolean
}

export interface SavedViewCreate {
  name: string
  identifier: FilterIdentifier
  shared: boolean
  payload: Record<any, any>
}
